import { useEffect, useRef } from 'react';
import BreadcrumbSection from './components/BreadcrumbSection/BreadcrumbSection';
import MoleculeHeaderSection from './components/MoleculeHeaderSection/MoleculeHeaderSection';
import AvailableStrengthsProductVariantsSection from './components/AvailableStrengthsProductVariantsSection/AvailableStrengthsProductVariantsSection';
import ANDAInformationSection from './components/ANDAInformationSection/ANDAInformationSection';
import MarketInsightsSection from './components/MarketInsightsSection/MarketInsightsSection';
import QuickNavigationSection from './components/QuickNavigationSection/QuickNavigationSection';
import MoleculeSearchSkeleton from './components/MoleculeSearchSkeleton/MoleculeSearchSkeleton';
import { useMoleculeSearchLogic } from './useMoleculeSearch.logic';
import '../global.styles.scss';
import './moleculeSearch.styles.scss';

const MoleculeSearch: React.FC = () => {
  const {
    // Main loading state (for initial page load)
    loading,
    // Molecule info and stats
    moleculeInfo,
    stats,
    moleculeInfoError,
    // Strengths
    strengths,
    strengthsLoading,
    strengthsError,
    // ANDA data
    andaData,
    andaDataLoading,
    andaDataError,
    // Market insights
    marketInsights,
    marketInsightsLoading,
    marketInsightsError,
  } = useMoleculeSearchLogic();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate wrapper height based on scaled content to prevent extra white space
  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current || loading || !moleculeInfo) return;

    const updateWrapperHeight = () => {
      // Use requestAnimationFrame to ensure calculation happens after render
      requestAnimationFrame(() => {
        if (!contentRef.current || !wrapperRef.current) return;

        const contentHeight = contentRef.current.scrollHeight || 0;
        // Since content is scaled to 65%, the visual height is 65% of the scrollHeight
        const scaledHeight = contentHeight * 0.65;
        wrapperRef.current.style.height = `${scaledHeight}px`;
      });
    };

    // Initial calculation with a small delay to ensure content is rendered
    const timeoutId = setTimeout(updateWrapperHeight, 100);

    // Update on window resize
    window.addEventListener('resize', updateWrapperHeight);

    // Use ResizeObserver to watch for content changes
    const resizeObserver = new ResizeObserver(() => {
      updateWrapperHeight();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWrapperHeight);
      resizeObserver.disconnect();
    };
  }, [loading, moleculeInfo, strengths, andaData, marketInsights]);

  if (loading) {
    return <MoleculeSearchSkeleton />;
  }

  if (!moleculeInfo || moleculeInfoError) {
    return (
      <>
        <div className="molecule-search-page-wrapper" ref={wrapperRef}>
          <div className="molecule-search-page" ref={contentRef}>
            <div
              className="container d-flex justify-content-center align-items-center"
              style={{ minHeight: '100vh' }}
            >
              <div className="text-center text-white">
                <h2>Error loading molecule data</h2>
                <p>{moleculeInfoError || 'Please try refreshing the page.'}</p>
              </div>
            </div>
          </div>
        </div>
        <QuickNavigationSection />
      </>
    );
  }

  return (
    <>
      <div className="molecule-search-page-wrapper" ref={wrapperRef}>
        <div className="molecule-search-page" ref={contentRef}>
          <BreadcrumbSection moleculeName={moleculeInfo.name} />
          <MoleculeHeaderSection
            moleculeInfo={moleculeInfo}
            stats={stats}
          />
          <main className="container">
            <AvailableStrengthsProductVariantsSection
              strengths={strengths}
              loading={strengthsLoading}
              error={strengthsError}
            />
            <ANDAInformationSection
              andaData={andaData}
              loading={andaDataLoading}
              error={andaDataError}
            />
            <MarketInsightsSection
              insights={marketInsights}
              loading={marketInsightsLoading}
              error={marketInsightsError}
            />
          </main>
        </div>
      </div>
      <QuickNavigationSection />
    </>
  );
};

export default MoleculeSearch;
